<template>
  <!-- Desktop admin rail. Two levels, straight off the one admin tree in
       config/adminNav.js — the same tree the breadcrumbs and the homepage
       boxes read, so none of the three can drift apart.

       One group open at a time, and the group holding the current page opens
       itself. That is what keeps a section you touch twice a month down to a
       single row the rest of the time, instead of six rows of permanent
       height between you and the one you use daily. -->
  <nav
    aria-label="Admin"
    :class="[
      'hidden lg:flex w-60 flex-shrink-0 flex-col border-r self-start sticky',
      'bg-(--nviya-surface) border-(--nviya-border)'
      ]"
    :style="{ top: topOffset, height: `calc(100vh - ${topOffset})` }"
  >
    <!-- 45 destinations is past the point where scanning works. -->
    <div class="px-2.5 py-2.5 flex-shrink-0">
      <NInput
        v-model="filter"
        size="sm"
        :icon="['fas', 'magnifying-glass']"
        placeholder="Find a page…"
      />
    </div>

    <div class="flex-1 overflow-y-auto rail-scroll pb-4">
      <!-- Filtering: one flat list, each hit carrying the group it lives in,
           because "where is it" is half the question being asked. -->
      <template v-if="query">
        <NuxtLink
          v-for="hit in hits"
          :key="hit.item.route"
          :to="hit.item.route"
          :class="rowClass(hit.item.route)"
        >
          <span class="flex-1 min-w-0 truncate">{{ hit.item.label }}</span>
          <span class="text-[10px] flex-shrink-0 text-(--nviya-text-tertiary)">{{ hit.group.label }}</span>
        </NuxtLink>
        <p v-if="!hits.length" class="px-5 py-2 text-sm text-(--nviya-text-tertiary)">
          Nothing matches.
        </p>
      </template>

      <template v-else>
        <NuxtLink :to="adminOverview.route" :class="rowClass(adminOverview.route)">
          <FontAwesome :icon="['fas', adminOverview.icon]" class="w-4 h-4 flex-shrink-0" />
          <span class="flex-1 min-w-0 truncate">{{ adminOverview.label }}</span>
        </NuxtLink>

        <template v-for="group in groups" :key="group.id">
          <button
            type="button"
            :class="[
              'w-[calc(100%-1rem)] flex items-center gap-3 mx-2 px-3 py-2 rounded-(--nviya-radius-md) text-sm transition-all',
              'n-row',
              isOpen(group)
                ? 'text-(--nviya-text-primary) font-semibold'
                : 'text-(--nviya-text-secondary) font-medium hover:text-(--nviya-text-primary)'
              ]"
            :aria-expanded="isOpen(group)"
            @click="toggle(group)"
          >
            <FontAwesome :icon="['fas', group.icon]" class="w-4 h-4 flex-shrink-0" />
            <span class="flex-1 min-w-0 truncate text-left">{{ group.label }}</span>
            <span class="text-[10px] flex-shrink-0 text-(--nviya-text-tertiary)">{{ group.items.length }}</span>
            <FontAwesome
              :icon="['fas', 'chevron-right']"
              :class="['w-2.5 h-2.5 flex-shrink-0 transition-transform', isOpen(group) ? 'rotate-90' : '']"
            />
          </button>

          <div v-show="isOpen(group)" class="pb-1">
            <NuxtLink
              v-for="item in group.items"
              :key="item.id"
              :to="item.route"
              :class="rowClass(item.route, true)"
              :title="item.label"
            >
              <span class="flex-1 min-w-0 truncate">{{ item.label }}</span>
            </NuxtLink>
          </div>
        </template>
      </template>
    </div>
  </nav>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { adminOverview, adminTree, adminLocate } from '@/config/adminNav'
import { usePermissions } from '@/composables/usePermissions'
import NInput from '@/components/ui/NInput.vue'

defineProps({
  // Where the fixed header ends — the rail sticks directly under it, and the
  // impersonation banner pushes everything down by another 2.5rem.
  topOffset: { type: String, default: '3.5rem' },
})

const route = useRoute()
const { can } = usePermissions()

// A group keeps only the pages this user may open, and a group left with
// nothing does not render at all.
const groups = computed(() =>
  adminTree
    .filter((g) => !g.permission || can(g.permission))
    .map((g) => ({ ...g, items: g.items.filter((i) => !i.permission || can(i.permission)) }))
    .filter((g) => g.items.length)
)

// Which group is open survives navigation, so clicking a page inside a group
// does not collapse the group you are working in.
const openGroup = useState('admin-rail-open-group', () => null)
const currentGroupId = computed(() => adminLocate(route.path)?.group.id || null)
watch(currentGroupId, (id) => { if (id) openGroup.value = id }, { immediate: true })

const isOpen = (group) => openGroup.value === group.id
const toggle = (group) => { openGroup.value = isOpen(group) ? null : group.id }

const filter = ref('')
const query = computed(() => filter.value.trim().toLowerCase())
const hits = computed(() => {
  if (!query.value) return []
  const q = query.value
  return groups.value.flatMap((group) =>
    group.items
      .filter((item) =>
        item.label.toLowerCase().includes(q)
        || group.label.toLowerCase().includes(q)
        || item.route.includes(q))
      .map((item) => ({ group, item }))
  )
})

// Match the most specific destination, as the breadcrumb does, so sibling
// menu entries with nested URLs do not both appear selected.
const isActive = (target) => target === adminOverview.route
  ? route.path === target
  : adminLocate(route.path)?.item.route === target

const rowClass = (target, indented = false) => ([
  'flex items-center gap-3 mx-2 py-2 rounded-(--nviya-radius-md) text-sm font-medium transition-all',
  indented ? 'pl-10 pr-3' : 'px-3',
  isActive(target)
    ? 'bg-(--nviya-selected-bg) text-(--nviya-accent-on-soft) font-semibold'
    : 'n-row text-(--nviya-text-secondary) hover:text-(--nviya-text-primary)',
])
</script>

<style scoped>
.rail-scroll {
  scroll-behavior: auto !important;
}
.rail-scroll::-webkit-scrollbar {
  width: 4px;
}
.rail-scroll::-webkit-scrollbar-track {
  background: transparent;
}
.rail-scroll::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.15);
  border-radius: var(--nviya-radius-sm);
}
.rail-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.25);
}
</style>
